import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { ConfirmConsultationModalProps } from './ConfirmConsultationModal.types'
import { DateTime } from 'luxon'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { useConfirmSchedule } from '../../ConsultationCard/queries'
import { useVerifyFreeAttendances } from '@/modules/MyCalls/queries'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { useAttendanceContext } from '@/modules/Attendance/contexts/AttendanceContex'
import Attendance from '@/modules/Attendance/classes/Attendance'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { setCache } from '@/utils/cache'
import { toast } from '@conexasaude/conexa-hero'
import { redirectToCheckout } from '@/modules/Checkout/utils/redirectToCheckout'
import { useHistory } from 'react-router-dom'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { useReservationConsultationContext } from '@/modules/MyCalls/contexts/consultationReservationContext'
import { Medic } from '@/modules/Attendance/classes/Medic'
import { SPECIALTY } from '@/@types/specialty'
import { KEYS_TO_INVALIDATE_WHEN_CONFIRMING } from './ConfirmConsultationModal.constants'
import { useConfirmReservationConsultationMutation } from '@/modules/Checkout/queries/checkout'

export const useConfirmConsultationModal = (
  props: ConfirmConsultationModalProps,
) => {
  const queryClient = useQueryClient()
  const history = useHistory()

  const { closeModal, showModal } = useModalContext()
  const { specialities } = useMedicalSpecialitiesContext()
  const { setAttendance } = useAttendanceContext()
  const { user, clinic } = useAuthContext()
  const { logAnalyticsEvent } = useFirebaseContext()
  const { setReservationConsultationData } = useReservationConsultationContext()

  const confirmScheduleMutation = useConfirmSchedule()
  const confirmReservationConsultationMutation =
    useConfirmReservationConsultationMutation()
  const verifyFreeAttendances = useVerifyFreeAttendances()

  const consultationDateParsed = useMemo(() => {
    if (!props?.consultation?.date) return

    return DateTime.fromFormat(props?.consultation?.date, 'dd/MM/yyyy HH:mm:ss')
  }, [props?.consultation?.date])

  const formattedDate = useMemo(() => {
    if (!props?.consultation?.date) {
      return {
        date: '',
        hour: '',
      }
    }

    const date = consultationDateParsed
      .setLocale('pt-BR')
      .toFormat("dd 'de' MMMM, EEEE")

    const hour = consultationDateParsed.toFormat('HH:mm')

    return {
      date,
      hour,
    }
  }, [consultationDateParsed, props?.consultation?.date])

  const isClinicUnifiedPVAndProfessionalIsPsychologist = useMemo(() => {
    return (
      clinic?.flag?.ClinicaUnificadaPV &&
      props?.professional?.professionalType === SPECIALTY.PSYCHOLOGIST
    )
  }, [clinic?.flag?.ClinicaUnificadaPV, props?.professional?.professionalType])

  const handleConfirmConsultation = useCallback(() => {
    if (isClinicUnifiedPVAndProfessionalIsPsychologist) {
      logAnalyticsEvent('Pac_Consultas_Confirmar')
    } else {
      logAnalyticsEvent('Pac_Home_ConfirmarConsulta')
    }

    confirmScheduleMutation.mutate(props?.consultation?.id, {
      onSuccess: () => {
        showModal('CONFIRMED_SCHEDULE', {
          title: 'Sua consulta foi confirmada',
          width: 512,
        })

        props.onConfirm?.()

        queryClient.invalidateQueries(['upcoming-consultation-list'])
        queryClient.invalidateQueries(['realtiment-appointment'])
      },
    })
  }, [
    confirmScheduleMutation,
    props,
    isClinicUnifiedPVAndProfessionalIsPsychologist,
    logAnalyticsEvent,
    queryClient,
    showModal,
  ])

  const handleRedirectToCheckoutWithTimeout = useCallback(() => {
    setTimeout(() => {
      redirectToCheckout({
        attendanceType: 'scheduled',
        professionalType: 'PSICOLOGO',
        push: history.push,
        hasPayment: clinic?.flag?.PermiteConsultaParticular,
        hasMultipleCards: clinic?.flag?.HabilitarMultiplosCartoesConvenio,
      })
    }, 1000)
  }, [
    clinic?.flag?.HabilitarMultiplosCartoesConvenio,
    clinic?.flag?.PermiteConsultaParticular,
    history.push,
  ])

  const handleConfirmReservation = useCallback(async () => {
    const consultationDatePTBR = consultationDateParsed.toFormat('dd-MM-yyyy')

    const specialty = specialities.find(
      s => s.id === props?.professional?.professionalType,
    )

    const hasFreeAttendances = await verifyFreeAttendances.mutateAsync({
      date: consultationDatePTBR,
      preferenceType: 'from_speciality',
      professionalType: props?.professional?.professionalType,
      attendanceType: 'AGENDADO',
      specialityId: props?.professional?.specialtyId,
    })

    const attendanceDateFormatted =
      consultationDateParsed.toFormat('dd/MM/yyyy')
    const attendanceHourFormatted = consultationDateParsed.toFormat('HH:mm')

    setAttendance(() => {
      const att = new Attendance(specialty)
      att.date = attendanceDateFormatted
      att.hour = attendanceHourFormatted
      att.patientContact = { email: user?.email, cellphone: user?.cellphone }
      att.scheduleId = props?.consultation?.id as number
      att.medic = new Medic(
        props?.professional.id,
        props?.professional.name,
        props?.professional?.photo,
        props?.professional?.specialty,
      )
      att.isReservation = true
      return att
    })

    setCache('has-free-attendance', hasFreeAttendances)

    const hasCheckoutEnabled = clinic?.flag?.PermiteConsultaParticular

    if (hasCheckoutEnabled && hasFreeAttendances === 'limitReached') {
      toast({
        message:
          'Realize o pagamento da consulta. Você atingiu o limite de atendimentos gratuitos. Finalize esse agendamento, realizando o pagamento.',
        variant: 'warning',
        autoCloseDuration: 5000,
      })

      handleRedirectToCheckoutWithTimeout()
      return
    }

    if (hasCheckoutEnabled && hasFreeAttendances !== 'limitNotReached') {
      handleRedirectToCheckoutWithTimeout()
      return
    }

    try {
      await confirmReservationConsultationMutation.mutateAsync({
        professional: {
          id: props?.professional?.id,
        },
        appointment: {
          id: props?.consultation?.id as number,
          date: consultationDateParsed.toFormat('dd/MM/yyyy HH:mm'),
        },
        user: {
          email: user?.email,
          phone: user?.cellphone,
        },
      })

      setReservationConsultationData(props?.default)

      props?.onConfirm?.()

      closeModal()
      KEYS_TO_INVALIDATE_WHEN_CONFIRMING.forEach(key =>
        queryClient.invalidateQueries([key]),
      )
      logAnalyticsEvent('Pac_Reserva_ConfirmarReserva')
    } catch (error) {
      const errorMessage =
        error?.response?.data?.msg || error?.response?.msg || error?.message

      toast({
        message: errorMessage,
        variant: 'error',
        autoCloseDuration: 5000,
      })
    }
  }, [
    clinic?.flag?.PermiteConsultaParticular,
    closeModal,
    confirmReservationConsultationMutation,
    consultationDateParsed,
    props,
    handleRedirectToCheckoutWithTimeout,
    logAnalyticsEvent,
    queryClient,
    setAttendance,
    setReservationConsultationData,
    specialities,
    user?.cellphone,
    user?.email,
    verifyFreeAttendances,
  ])

  const handlePrimaryAction = useCallback(() => {
    if (
      props?.consultation?.isReservation &&
      !props?.consultation?.isReservationConfirmed
    ) {
      handleConfirmReservation()
      return
    }

    handleConfirmConsultation()
  }, [
    props?.consultation?.isReservation,
    props?.consultation?.isReservationConfirmed,
    handleConfirmConsultation,
    handleConfirmReservation,
  ])

  const primaryActionIsLoading = useMemo(() => {
    return (
      confirmScheduleMutation.isLoading ||
      confirmReservationConsultationMutation.isLoading
    )
  }, [
    confirmReservationConsultationMutation.isLoading,
    confirmScheduleMutation.isLoading,
  ])

  return {
    formattedDate,
    actions: {
      handlePrimaryAction,
      closeModal,
    },
    loadings: {
      primaryActionIsLoading,
    },
  }
}
